import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { MapPin, List, Map, Lightbulb, Maximize, TrendingUp, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO, { breadcrumbSchema } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LocationsPage = () => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchLocations();
    fetchSettings();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/locations`);
      const data = await response.json();
      setLocations(data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/public`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-8 lg:py-12">
      <SEO
        title="Standorte"
        description="Alle verfügbaren Werbestandorte im Zürcher Oberland. Finden Sie den idealen Platz für Ihre mobile Werbung – mit Karte und Passantenfrequenz."
        canonicalPath="/locations"
        structuredData={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Standorte', path: '/locations' }
        ])}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with value proposition */}
        <div className="mb-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <h1 className="text-3xl sm:text-4xl font-semibold mb-3">Unsere Standorte im Zürcher Oberland</h1>
            <p className="text-[hsl(var(--muted-foreground))] text-lg max-w-2xl mb-4">
              Wählen Sie Ihren Wunschstandort – jeder Platz ist strategisch platziert für maximale Sichtbarkeit bei Ihrer Zielgruppe.
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-[hsl(var(--muted-foreground))]">
              {['Alle Preise transparent', 'Verfügbarkeit in Echtzeit', 'Online buchbar'].map((item, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[hsl(var(--chart-2))]" />
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-[hsl(var(--muted-foreground))]">
            {locations.length} {t('locations.title')}
          </div>
          <Tabs value={viewMode} onValueChange={setViewMode}>
            <TabsList>
              <TabsTrigger value="list" data-testid="locations-view-list">
                <List className="w-4 h-4 mr-2" />
                {t('locations.listView')}
              </TabsTrigger>
              <TabsTrigger value="map" data-testid="locations-view-map">
                <Map className="w-4 h-4 mr-2" />
                {t('locations.mapView')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content */}
        {locations.length === 0 ? (
          <div className="empty-state">
            <MapPin className="empty-state-icon" />
            <h3 className="text-lg font-semibold mb-2">{t('locations.noLocations')}</h3>
            <p className="text-sm">Es wurden noch keine Standorte hinzugefügt.</p>
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="locations-list">
            {locations.map((location, index) => {
              const isUnavailable = location.effective_available === false;
              return (
              <motion.div
                key={location.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link to={`/locations/${location.id}`} data-testid={`location-card-${location.id}`}>
                  <Card className={`overflow-hidden card-shadow transition-all duration-200 cursor-pointer h-full ${isUnavailable ? 'opacity-60 grayscale' : 'card-shadow-hover'}`}>
                    <div className="relative h-48 bg-[hsl(var(--muted))]">
                      {location.images && location.images.length > 0 ? (
                        <img
                          src={location.images[0]}
                          alt={`Werbestandort ${location.name}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src="https://images.unsplash.com/photo-1617144078443-95018ade4ee9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600"
                          alt={`Werbestandort ${location.name}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                      {isUnavailable ? (
                        <Badge className="absolute top-3 right-3 bg-[hsl(var(--muted-foreground))] text-white border-0 text-xs" data-testid={`location-unavailable-badge-${location.id}`}>
                          Aktuell nicht verfügbar
                        </Badge>
                      ) : (
                        <Badge className="absolute top-3 right-3 bg-[hsl(var(--chart-2))] text-white border-0 text-xs">
                          Verfügbar
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-lg font-semibold mb-1">{location.name}</h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3 flex items-center">
                        <MapPin className="w-4 h-4 mr-1 flex-shrink-0" />
                        {location.address}
                      </p>
                      {location.traffic_frequency && (
                        <div className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] mb-3">
                          <TrendingUp className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                          {location.traffic_frequency}
                        </div>
                      )}
                      {location.trailer && (
                        <div className="flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))] mb-4">
                          <span className="flex items-center">
                            <Maximize className="w-3 h-3 mr-1" />
                            {location.trailer.size_m2}m²
                          </span>
                          {location.trailer.has_lighting && (
                            <span className="flex items-center">
                              <Lightbulb className="w-3 h-3 mr-1" />
                              LED
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-3 border-t border-[hsl(var(--border))]">
                        <div>
                          <span className="text-xl font-semibold">CHF {formatPrice(location.price_per_week)}</span>
                          <span className="text-sm text-[hsl(var(--muted-foreground))]"> /Woche</span>
                        </div>
                        <Button size="sm" className="gap-1">
                          Details
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
              );
            })}
          </div>
        ) : (
          /* Map View */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card className="overflow-hidden" data-testid="locations-google-map">
                <div className="h-[500px] bg-[hsl(var(--muted))] flex items-center justify-center">
                  {settings?.google_maps_api_key ? (
                    <div className="w-full h-full">
                      {/* Google Maps would be loaded here */}
                      <div className="w-full h-full flex items-center justify-center text-[hsl(var(--muted-foreground))]">
                        <div className="text-center">
                          <Map className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Karte wird geladen...</p>
                          <p className="text-sm mt-2">Standorte: {locations.length}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-[hsl(var(--muted-foreground))]">
                      <Map className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Google Maps API Key nicht konfiguriert</p>
                      <p className="text-sm mt-2">Bitte im Admin-Bereich hinterlegen</p>
                    </div>
                  )}
                </div>
              </Card>
            </div>
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {locations.map((location) => (
                <Link key={location.id} to={`/locations/${location.id}`}>
                  <Card className="p-4 card-shadow hover:bg-[hsl(var(--muted))] transition-colors cursor-pointer">
                    <h4 className="font-semibold mb-1">{location.name}</h4>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{location.address}</p>
                    <p className="font-semibold">CHF {formatPrice(location.price_per_week)}{t('locations.perWeek')}</p>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationsPage;
